// Browser-native country + language hints. Used by both:
//   - components/LanguageWelcome — to suggest a UI language on first launch
//   - containers/AddPostPage     — to pre-select the seller's local currency
//
// Two signals stack inside `getBrowserCountry()`:
//   1. Intl.Locale(navigator.language).region — catches users with explicit
//      country-coded browsers (en-IN, en-PK, es-MX, …). Works for *any*
//      country the user's OS knows about, not just the shortlist below.
//   2. OS timezone via Intl.DateTimeFormat — catches the very common
//      "en-US default browser, but physically somewhere else" case (a fresh
//      phone in Karachi reporting `Asia/Karachi`). Scoped to the timezone
//      map below; expand it if a new market shows up in the analytics.

// IANA timezone → ISO 3166-1 alpha-2 country (uppercase). Sized to the
// countries whose primary language is one of our 7 non-en locales, plus
// every timezone alias commonly returned by older OSes.
export const TIMEZONE_TO_COUNTRY = {
  // India
  'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN',
  // Pakistan
  'Asia/Karachi': 'PK',
  // Indonesia (WIB / WITA / WIT)
  'Asia/Jakarta': 'ID', 'Asia/Pontianak': 'ID',
  'Asia/Makassar': 'ID', 'Asia/Jayapura': 'ID',
  // Spain (peninsula + Canaries + Ceuta)
  'Europe/Madrid': 'ES', 'Atlantic/Canary': 'ES', 'Africa/Ceuta': 'ES',
  // France + Monaco
  'Europe/Paris': 'FR', 'Europe/Monaco': 'MC',
  // Germany + Austria (incl. Busingen exclave)
  'Europe/Berlin': 'DE', 'Europe/Busingen': 'DE', 'Europe/Vienna': 'AT',
  // Portugal (mainland + Madeira + Azores)
  'Europe/Lisbon': 'PT', 'Atlantic/Madeira': 'PT', 'Atlantic/Azores': 'PT',
  // Mexico
  'America/Mexico_City': 'MX', 'America/Cancun': 'MX', 'America/Merida': 'MX',
  'America/Monterrey': 'MX', 'America/Matamoros': 'MX', 'America/Mazatlan': 'MX',
  'America/Chihuahua': 'MX', 'America/Ojinaga': 'MX', 'America/Hermosillo': 'MX',
  'America/Tijuana': 'MX', 'America/Bahia_Banderas': 'MX',
  // Argentina
  'America/Argentina/Buenos_Aires': 'AR', 'America/Argentina/Cordoba': 'AR',
  'America/Argentina/Salta': 'AR', 'America/Argentina/Jujuy': 'AR',
  'America/Argentina/Tucuman': 'AR', 'America/Argentina/Catamarca': 'AR',
  'America/Argentina/La_Rioja': 'AR', 'America/Argentina/San_Juan': 'AR',
  'America/Argentina/Mendoza': 'AR', 'America/Argentina/San_Luis': 'AR',
  'America/Argentina/Rio_Gallegos': 'AR', 'America/Argentina/Ushuaia': 'AR',
  // Brazil
  'America/Sao_Paulo': 'BR', 'America/Manaus': 'BR', 'America/Rio_Branco': 'BR',
  'America/Fortaleza': 'BR', 'America/Recife': 'BR', 'America/Bahia': 'BR',
  'America/Belem': 'BR', 'America/Cuiaba': 'BR', 'America/Maceio': 'BR',
  'America/Noronha': 'BR', 'America/Eirunepe': 'BR', 'America/Araguaina': 'BR',
  'America/Boa_Vista': 'BR', 'America/Campo_Grande': 'BR',
  'America/Porto_Velho': 'BR', 'America/Santarem': 'BR',
  // LatAm Spanish singletons
  'America/Bogota': 'CO',
  'America/Lima': 'PE',
  'America/Santiago': 'CL', 'America/Punta_Arenas': 'CL', 'Pacific/Easter': 'CL',
  'America/Caracas': 'VE',
  'America/Guayaquil': 'EC', 'Pacific/Galapagos': 'EC',
  'America/Havana': 'CU',
  'America/Guatemala': 'GT',
  'America/La_Paz': 'BO',
  'America/Santo_Domingo': 'DO',
  'America/Tegucigalpa': 'HN',
  'America/Asuncion': 'PY',
  'America/El_Salvador': 'SV',
  'America/Managua': 'NI',
  'America/Costa_Rica': 'CR',
  'America/Panama': 'PA',
  'America/Montevideo': 'UY',
  'America/Puerto_Rico': 'PR',
};

// Country whose primary language is one of our 7 non-English locales we
// ship. Deliberately omits genuinely multilingual countries (Switzerland,
// Belgium, Canada, Luxembourg). PT folds into pt-BR same way the existing
// normalizeLanguage folds pt-* browser codes.
export const COUNTRY_TO_LANGUAGE = {
  // Spanish-speaking countries
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', PE: 'es', CL: 'es', VE: 'es',
  EC: 'es', GT: 'es', CU: 'es', BO: 'es', DO: 'es', HN: 'es', PY: 'es',
  SV: 'es', NI: 'es', CR: 'es', PA: 'es', UY: 'es', PR: 'es',
  // Portuguese
  BR: 'pt-BR', PT: 'pt-BR',
  // German
  DE: 'de', AT: 'de',
  // French (single-language ships only)
  FR: 'fr', MC: 'fr',
  // Hindi / Urdu / Indonesian
  IN: 'hi', PK: 'ur', ID: 'id',
};

// Best-effort browser country detection. Returns a *lowercase* ISO 3166-1
// alpha-2 code (matches the existing userCountry / localStorage 'country'
// convention) or null when neither signal yields a known country.
//
// Order matters: timezone wins over Intl.Locale region because the OS
// timezone tracks *physical location* while the region tracks the
// browser's configured *preference*. A Pakistani user on a phone shipped
// with the en-US default has region='US' but timezone='Asia/Karachi' —
// PK is the right answer for currency (they spend PKR) and for the
// language modal (Urdu suggested over English). The TIMEZONE_TO_COUNTRY
// map is narrow enough that any hit is a strong signal; falling through
// to region when there's no timezone match catches users in countries
// we don't language-detect for (UK, Australia, Japan, etc.) where region
// is still a perfectly fine currency hint.
export function getBrowserCountry() {
  // 1. OS timezone — physical location, narrow but precise.
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const country = tz && TIMEZONE_TO_COUNTRY[tz];
    if (country) return country.toLowerCase();
  } catch {
    // older browsers / sandboxed contexts may throw; fall through.
  }

  // 2. Browser locale region fallback (e.g. 'en-GB' → 'GB', 'en-AU' → 'AU').
  //    Reached only when timezone is outside our narrow map — typically
  //    countries where any browser-set region is the user's best signal.
  try {
    const region = new Intl.Locale(navigator.language || 'en').region;
    if (region) return region.toLowerCase();
  } catch {
    // Intl.Locale unsupported — bail gracefully.
  }

  return null;
}
